import React, { useEffect, useState } from 'react';
import { get_departments, fetchUserRoles } from '../../../helper/Api';
import DataTable from 'react-data-table-component';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Modal } from 'react-bootstrap';
import { IconButton, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import './ManageDepartment.css';
import Spinner from '../../Tools/spinner';
import { DepartmentDistrictBadge } from '../../Tools/DepartmentIdentityCell';

/** Match Consolidate PPA Report table viewport fill (`maxHeight: 72vh`). */
const TABLE_BODY_SCROLL_HEIGHT = 'calc(72vh - 48px)';

const ColHeader = ({ children }) => (
  <div className="manage-department-col-header">{children}</div>
);

const StatusPill = ({ label, bg, color }) => (
  <span className="manage-department-pill" style={{ backgroundColor: bg, color }}>
    {label}
  </span>
);

const ManageDepartment = ({ searchStr, tier, district_id = '' }) => {
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const { authData } = useSelector((state) => state.authData);
  const [departments, setDepartments] = useState([]);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [userRolesData, setUserRolesData] = useState([]);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const response = await get_departments({
        department_level: tier,
        searchStr: searchStr,
        parent_department_id:
          authData.user.department_id != 1 ? authData.user.department_id : null,
        district_id: district_id || '',
      });
      if (response.data.status) {
        setDepartments(response.data.result || []);
      } else {
        setDepartments([]);
      }
    } catch (error) {
      setDepartments([]);
      toast.error(error?.message || 'Failed to fetch departments', {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewUsers = async (row) => {
    setSelectedDepartment(row);
    setShowUsersModal(true);
    setUsersLoading(true);
    setUserRolesData([]);

    try {
      const response = await fetchUserRoles(row.id);
      if (response?.data?.status) {
        setUserRolesData(response?.data?.result || []);
      } else {
        toast.error(response?.data?.message || 'Failed to fetch user roles', {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to fetch user roles', {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchStr, tier, district_id]);

  const customStyles = {
    headCells: {
      style: {
        fontWeight: '700',
        fontSize: '13px',
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'clip',
        lineHeight: 1.35,
        paddingTop: '10px',
        paddingBottom: '10px',
        color: '#28326f',
      },
    },
    cells: {
      style: {
        fontSize: '13px',
        paddingTop: '10px',
        paddingBottom: '10px',
      },
    },
  };

  const getPaymentModeMeta = (paymentMode) => {
    const mode = String(paymentMode || '').toLowerCase();

    if (mode === '1' || mode === 'online') {
      return { label: 'Online', bg: '#e6f4ea', color: '#1e7e34' };
    }
    if (mode === '2' || mode === 'offline') {
      return { label: 'Offline', bg: '#fff4e5', color: '#b26a00' };
    }
    if (mode === '3' || mode === 'both') {
      return { label: 'Both', bg: '#e8f0ff', color: '#1f4db8' };
    }
    return { label: 'N/A', bg: '#f1f3f5', color: '#5f6368' };
  };

  const getTierMeta = (tierValue) => {
    const value = String(tierValue || '').toUpperCase();
    if (value === 'T1') return { label: 'T1', bg: '#e8f0ff', color: '#1f4db8' };
    if (value === 'T2') return { label: 'T2', bg: '#e6f4ea', color: '#1e7e34' };
    if (value === 'T3') return { label: 'T3', bg: '#fff4e5', color: '#b26a00' };
    if (value === 'T4') return { label: 'T4', bg: '#f3e8ff', color: '#6b21a8' };
    return { label: value || 'N/A', bg: '#f1f3f5', color: '#5f6368' };
  };

  const getRoleMeta = (roleValue) => {
    const role = String(roleValue || '').toLowerCase();

    if (role === 'l1' || role === '1') {
      return { label: 'L1', bg: '#e8f0ff', color: '#1f4db8' };
    }
    if (role === 'l2' || role === '2') {
      return { label: 'L2', bg: '#fff4e5', color: '#b26a00' };
    }
    if (role === 'l3' || role === '3') {
      return { label: 'L3', bg: '#e6f4ea', color: '#1e7e34' };
    }
    return { label: 'N/A', bg: '#f1f3f5', color: '#5f6368' };
  };

  const canEditRow = (row) => {
    if (authData.user.user_type !== 'l3') return false;
    if (
      authData.user.department_details.department_level !== 'T1' &&
      authData.user.department_details.id == row.id
    ) {
      return false;
    }
    return true;
  };

  const columns = [
    {
      name: <ColHeader>Department</ColHeader>,
      selector: (row) => row.name,
      cell: (row) => (
        <div>
          <div className="manage-department-name">{row.name || '-'}</div>
          <div className="manage-department-code">{row.code || '-'}</div>
        </div>
      ),
      sortable: true,
      wrap: true,
      minWidth: '220px',
      grow: 2,
    },
    {
      name: <ColHeader>Level</ColHeader>,
      selector: (row) => row.nomiclature,
      cell: (row) => row.nomiclature || <span className="manage-department-muted">-</span>,
      sortable: true,
      wrap: true,
      minWidth: '120px',
    },
    {
      name: <ColHeader>Tier</ColHeader>,
      selector: (row) => row.department_level,
      cell: (row) => {
        const tierMeta = getTierMeta(row.department_level);
        return <StatusPill {...tierMeta} />;
      },
      sortable: true,
      center: true,
      width: '90px',
    },
    {
      name: <ColHeader>District</ColHeader>,
      selector: (row) => row.district_name || '',
      cell: (row) => {
        if (!row.district_name) {
          return <span className="manage-department-muted">-</span>;
        }
        return <DepartmentDistrictBadge row={row} />;
      },
      sortable: true,
      wrap: true,
      minWidth: '140px',
    },
    {
      name: <ColHeader>Payment Mode</ColHeader>,
      selector: (row) => getPaymentModeMeta(row.payment_mode).label,
      cell: (row) => <StatusPill {...getPaymentModeMeta(row.payment_mode)} />,
      sortable: true,
      center: true,
      minWidth: '120px',
    },
    {
      name: <ColHeader>Parent Department</ColHeader>,
      selector: (row) => row.parent_department_name,
      cell: (row) => {
        if (!row.parent_department_name) {
          return <span className="manage-department-muted">-</span>;
        }
        return (
          <div>
            <div className="manage-department-parent-name">
              {row.parent_department_name}
            </div>
            {row.parent_department_level && (
              <div className="manage-department-parent-tier">
                Tier {row.parent_department_level}
              </div>
            )}
          </div>
        );
      },
      sortable: true,
      wrap: true,
      minWidth: '180px',
      grow: 1,
    },
    {
      name: <ColHeader>Users</ColHeader>,
      cell: (row) => (
        <button
          type="button"
          className="manage-department-view-users"
          onClick={() => handleViewUsers(row)}
        >
          View Users
        </button>
      ),
      center: true,
      minWidth: '120px',
      ignoreRowClick: true,
      button: true,
    },
  ];

  if (authData.user.user_type === 'l3') {
    columns.push({
      name: <ColHeader>Action</ColHeader>,
      cell: (row) => {
        if (!canEditRow(row)) {
          return <span className="manage-department-muted">-</span>;
        }
        return (
          <div className="manage-department-actions">
            <Tooltip title="Edit Department" arrow>
              <IconButton
                component={Link}
                to={`/edit-department/${row.id}`}
                size="small"
                sx={{
                  width: 28,
                  height: 28,
                  border: '1px solid #ed6c02',
                  color: '#ed6c02',
                  borderRadius: '6px',
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
      center: true,
      width: '90px',
      ignoreRowClick: true,
      button: true,
    });
  }

  const handleChangeRowsPerPage = (currentRowsPerPage) => {
    setRowsPerPage(currentRowsPerPage);
    setCurrentPage(1);
  };

  return (
    <div className="manage-department-datatable-root">
      {loading && (
        <div>
          <Spinner />
        </div>
      )}
      <DataTable
        customStyles={customStyles}
        className="dataTables_wrapper"
        columns={columns}
        data={departments}
        persistTableHead
        fixedHeader
        fixedHeaderScrollHeight={TABLE_BODY_SCROLL_HEIGHT}
        pagination
        paginationPerPage={rowsPerPage}
        paginationDefaultPage={currentPage}
        paginationTotalRows={departments.length}
        paginationRowsPerPageOptions={[25, 50, 100]}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        onChangePage={setCurrentPage}
        noDataComponent={
          <div className="p-4 text-muted">No departments found.</div>
        }
      />

      <Modal
        show={showUsersModal}
        onHide={() => setShowUsersModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            View Users
            {selectedDepartment?.name ? ` — ${selectedDepartment.name}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {usersLoading ? (
            <p className="mb-0">Loading users...</p>
          ) : (
            <div className="table-responsive">
              <table
                className="table table-bordered table-striped mb-0"
                style={{ tableLayout: 'fixed', width: '100%' }}
              >
                <thead>
                  <tr>
                    <th style={{ width: '10%' }}>S.No.</th>
                    <th style={{ width: '30%' }}>User Info</th>
                    <th style={{ width: '45%' }}>Contact Info</th>
                    <th style={{ width: '15%' }}>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {userRolesData.length > 0 ? (
                    userRolesData.map((user, index) => (
                      <tr key={user?.id || index}>
                        <td>{index + 1}</td>
                        <td>
                          <strong>{user?.user_name}</strong>
                          <br />
                          {user?.designation}
                        </td>
                        <td>
                          <strong>{user?.email || '-'}</strong>
                          <br />
                          {user?.mobile}
                        </td>
                        <td>
                          {(() => {
                            const role = getRoleMeta(
                              user?.role || user?.user_role || user?.user_type
                            );
                            return <StatusPill {...role} />;
                          })()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center">
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ManageDepartment;
